import {
  getScheduledDriveStatisticsOptions,
  getPackageSubmissionDetailOptions,
} from '@/api/main/@tanstack/react-query.gen'
import ErrorState from '@/components/Common/ErrorState'
import DriveDetailsTable from '@/components/DriveDetails/DriveDetailsTable'
import { createFileRoute } from '@tanstack/react-router'
import { requirePermission } from '@/utils/auth'
import PageShell from '@/components/Common/PageShell'
import { usePageTitle } from '@/hooks/usePageTitle'
import { CITY_NAME } from '@/meta'
import DriveDetailsMap from '@/components/DriveDetails/DriveDetailsMap'
import { useSuspenseQuery, QueryErrorResetBoundary } from '@tanstack/react-query'
import { Suspense } from 'react'
import { RouteLoading } from '@/components/Common/RouteLoader'
import { ErrorBoundary } from 'react-error-boundary'
import { Button, Alert, Text } from '@mantine/core'
import { TbReload } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

export const Route = createFileRoute('/drive-details/$id')({
  beforeLoad: () => requirePermission('drive_management'),
  component: () => (
    <Suspense fallback={<RouteLoading title={i18next.t('driveDetailsLoading', { ns: 'driveDetails' })} />}>
      <DriveDetailsPage />
    </Suspense>
  ),
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('errorLoadingDriveDetails', { ns: 'driveDetails' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoadDriveDetails', { ns: 'driveDetails' })}
        message={error.message || i18next.t('unableToLoadDriveDetails', { ns: 'driveDetails' })}
      />
    </PageShell>
  ),
})

const MapErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => (
  <Alert
    icon={<TbReload size={16} />}
    title={i18next.t('failedToLoadMap', { ns: 'driveDetails' })}
    color="red"
    variant="light"
    mb="md"
  >
    <Text size="sm" mb="md">
      {error.message}
    </Text>
    <Button leftSection={<TbReload size={16} />} variant="light" onClick={resetErrorBoundary} size="xs">
      {i18next.t('retry', { ns: 'common' })}
    </Button>
  </Alert>
)

const MapLoadingFallback = () => (
  <div style={{ height: '50em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <RouteLoading title={i18next.t('loadingMap', { ns: 'driveDetails' })} />
  </div>
)

function DriveDetailsPage() {
  const { id } = Route.useParams()
  const { t } = useTranslation('driveDetails')

  usePageTitle(`${t('driveDetails')} - ${id} | ${CITY_NAME}`)

  const { data: driveStatistics } = useSuspenseQuery(
    getScheduledDriveStatisticsOptions({ path: { drive_id: Number(id) } })
  )

  const { data: packageSubmissionDetail } = useSuspenseQuery(
    getPackageSubmissionDetailOptions({ path: { drive_id: Number(id) } })
  )

  if (!driveStatistics) {
    return (
      <PageShell title={`${t('driveDetails')} - ${id}`}>
        <ErrorState title={t('driveNotFound')} message={t('driveNotFoundMessage')} />
      </PageShell>
    )
  }

  const isInactive = ['finished', 'cancelled', 'cancelled_in_progress'].includes(driveStatistics.state)

  return (
    <PageShell title={`${t('driveDetails')} - ${id}`}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallbackRender={({ error, resetErrorBoundary }) => (
              <MapErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
            )}
          >
            <Suspense fallback={<MapLoadingFallback />}>
              <DriveDetailsMap
                vehicleId={driveStatistics.vehicle_id}
                showPatrolVehiclePosition={!isInactive}
                driveId={id}
                isInactive={isInactive}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
      <DriveDetailsTable driveStatistics={driveStatistics} packageSubmissionDetail={packageSubmissionDetail} />
    </PageShell>
  )
}
